// src/hooks/useSound.js – Cinematic Web Audio Engine (procedural, no files)
import { useCallback, useRef } from 'react';

let _sharedCtx = null;
let _masterGain = null;
let _bgGain = null;
let _bgPlaying = false;

function getCtx() {
  if (!_sharedCtx) {
    try {
      _sharedCtx = new (window.AudioContext || window.webkitAudioContext)();
      _masterGain = _sharedCtx.createGain();
      _masterGain.gain.value = 0.8;
      _masterGain.connect(_sharedCtx.destination);
    } catch { return null; }
  }
  if (_sharedCtx.state === 'suspended') _sharedCtx.resume().catch(() => {});
  return _sharedCtx;
}

function tone(freq, dur, type, vol) {
  const c = getCtx(); if (!c) return;
  try {
    const osc = c.createOscillator(), g = c.createGain();
    osc.connect(g); g.connect(_masterGain);
    osc.type = type || 'square';
    osc.frequency.setValueAtTime(freq, c.currentTime);
    g.gain.setValueAtTime(vol || 0.1, c.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, c.currentTime + (dur || 0.1));
    osc.start(c.currentTime);
    osc.stop(c.currentTime + (dur || 0.1) + 0.02);
  } catch {}
}

function noise(dur, vol, freq) {
  const c = getCtx(); if (!c) return;
  try {
    const len = Math.floor(c.sampleRate * Math.min(dur || 0.1, 3));
    const buf = c.createBuffer(1, len, c.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    const src = c.createBufferSource(); src.buffer = buf;
    const filt = c.createBiquadFilter();
    filt.type = 'bandpass'; filt.frequency.value = freq || 2000; filt.Q.value = 2;
    const g = c.createGain();
    g.gain.setValueAtTime(vol || 0.05, c.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, c.currentTime + (dur || 0.1));
    src.connect(filt); filt.connect(g); g.connect(_masterGain);
    src.start();
  } catch {}
}

export function useSound() {
  const playCinematicStart = useCallback(() => {
    const c = getCtx(); if (!c) return;
    const t = c.currentTime;
    // Deep bass impacts
    [55, 73, 98, 44].forEach((f, i) => {
      const osc = c.createOscillator(), g = c.createGain();
      osc.connect(g); g.connect(_masterGain);
      osc.type = 'sine'; osc.frequency.setValueAtTime(f, t + i * 0.045);
      g.gain.setValueAtTime(0.22, t + i * 0.045);
      g.gain.exponentialRampToValueAtTime(0.001, t + i * 0.045 + 1.3);
      osc.start(t + i * 0.045); osc.stop(t + i * 0.045 + 1.4);
    });
    // Rising arpeggio
    [261, 329, 392, 523, 659, 784, 1047, 1319].forEach((f, i) => {
      const when = t + 0.28 + i * 0.09;
      const osc = c.createOscillator(), g = c.createGain();
      osc.connect(g); g.connect(_masterGain);
      osc.type = 'triangle'; osc.frequency.setValueAtTime(f, when);
      g.gain.setValueAtTime(0.001, when);
      g.gain.linearRampToValueAtTime(0.09, when + 0.04);
      g.gain.exponentialRampToValueAtTime(0.001, when + 0.38);
      osc.start(when); osc.stop(when + 0.42);
    });
    // Grand finale chord
    [523, 659, 784, 1047].forEach((f, i) => {
      const when = t + 1.15;
      const osc = c.createOscillator(), g = c.createGain();
      osc.connect(g); g.connect(_masterGain);
      osc.type = 'sine'; osc.frequency.setValueAtTime(f, when);
      g.gain.setValueAtTime(0.001, when);
      g.gain.linearRampToValueAtTime(0.07, when + 0.22);
      g.gain.exponentialRampToValueAtTime(0.001, when + 2.8);
      osc.start(when); osc.stop(when + 2.9);
    });
    noise(2.2, 0.03, 800);
  }, []);

  const startBgMusic = useCallback(() => {
    if (_bgPlaying) return;
    const c = getCtx(); if (!c) return;
    _bgPlaying = true;
    _bgGain = c.createGain(); _bgGain.gain.value = 0;
    _bgGain.connect(_masterGain);
    _bgGain.gain.linearRampToValueAtTime(0.038, c.currentTime + 5);
    const SCALE = [65.41, 73.42, 87.31, 98, 130.81, 164.81, 196];
    let loop = 0;
    const sched = () => {
      if (!_bgPlaying || !_sharedCtx || !_bgGain) return;
      const ct = _sharedCtx.currentTime;
      const bLen = 0.54;
      const seq = [0, 3, 5, 2, 4, 6, 3, 1];
      seq.forEach((ni, beat) => {
        const when = ct + beat * bLen;
        const osc = _sharedCtx.createOscillator(), g = _sharedCtx.createGain();
        osc.connect(g); g.connect(_bgGain);
        osc.type = 'sine'; osc.frequency.value = SCALE[ni % SCALE.length];
        g.gain.setValueAtTime(0.001, when);
        g.gain.linearRampToValueAtTime(0.38, when + 0.05);
        g.gain.exponentialRampToValueAtTime(0.001, when + bLen * 0.68);
        osc.start(when); osc.stop(when + bLen + 0.05);
      });
      [196, 246, 293].forEach(f => {
        const osc = _sharedCtx.createOscillator(), g = _sharedCtx.createGain(), filt = _sharedCtx.createBiquadFilter();
        filt.type = 'lowpass'; filt.frequency.value = 480;
        osc.connect(filt); filt.connect(g); g.connect(_bgGain);
        osc.type = 'sawtooth'; osc.frequency.value = f; osc.detune.value = (loop % 3) * 6;
        g.gain.setValueAtTime(0.07, ct); g.gain.linearRampToValueAtTime(0.13, ct + 0.5);
        g.gain.exponentialRampToValueAtTime(0.001, ct + seq.length * bLen);
        osc.start(ct); osc.stop(ct + seq.length * bLen + 0.1);
      });
      loop++;
      setTimeout(sched, seq.length * bLen * 1000 - 120);
    };
    setTimeout(sched, 1400);
  }, []);

  const stopBgMusic = useCallback(() => {
    _bgPlaying = false;
    if (_bgGain && _sharedCtx) {
      _bgGain.gain.linearRampToValueAtTime(0, _sharedCtx.currentTime + 1.5);
    }
  }, []);

  const playSuccess  = useCallback(() => { tone(523,.12,'triangle',.1); setTimeout(()=>tone(659,.12,'triangle',.1),100); setTimeout(()=>tone(784,.18,'triangle',.1),200); setTimeout(()=>{ tone(1047,.3,'triangle',.12); noise(.06,.03,3000); },340); }, []);
  const playError    = useCallback(() => { tone(200,.09,'sawtooth',.1); setTimeout(()=>tone(150,.16,'sawtooth',.08),80); noise(.1,.04,400); }, []);
  const playCoin     = useCallback(() => { tone(1047,.07,'sine',.09); setTimeout(()=>tone(1319,.12,'sine',.09),65); setTimeout(()=>tone(1568,.1,'triangle',.07),135); }, []);
  const playLevelUp  = useCallback(() => { [523,659,784,1047,1319].forEach((n,i)=>setTimeout(()=>tone(n,.15,'triangle',.1),i*78)); setTimeout(()=>tone(1568,.42,'square',.08),390); setTimeout(()=>tone(2093,.55,'sine',.06),620); }, []);
  const playClick    = useCallback(() => { tone(880,.04,'square',.04); noise(.03,.012,4200); }, []);
  const playHover    = useCallback(() => tone(1300,.022,'sine',.014), []);
  const playBadge    = useCallback(() => { tone(659,.1,'triangle',.1); setTimeout(()=>tone(784,.1,'triangle',.1),100); setTimeout(()=>{ tone(988,.24,'triangle',.12); noise(.1,.03,2200); },205); }, []);
  const playTransition = useCallback(() => { noise(.13,.028,1500); tone(440,.16,'sine',.04); setTimeout(()=>tone(880,.1,'sine',.03),85); }, []);
  const playImpact   = useCallback(() => { noise(.16,.09,200); tone(80,.22,'sawtooth',.13); setTimeout(()=>noise(.08,.04,600),55); }, []);

  return { playSuccess, playError, playCoin, playLevelUp, playClick, playHover, playBadge, playTransition, playImpact, playCinematicStart, startBgMusic, stopBgMusic };
}
